import { getAccessToken } from '@/store/session'
import type { ApiError } from '@/types'

/**
 * Cliente HTTP centralizado del frontend MapeoData.
 *
 * Todas las llamadas al backend MapeoData pasan por aquí. Inyecta
 * automáticamente:
 * - Prefijo `/api/v1/` para todos los endpoints de negocio (DEC-010
 *   commitment versionado + cero breaking changes en v1 + ventana
 *   migración 60 días).
 * - `Authorization: Bearer ${jwt}` con el token del usuario logueado
 *   vía GoTrue MapeoData (DEC-003 auth separado por proyecto).
 * - `Content-Type: application/json` para POST/PUT/PATCH con body.
 *
 * Errores del backend se devuelven con shape estandarizado `ApiError`
 * (`{error, message, code}`) y el cliente los normaliza a una excepción
 * tipada que cualquier consumidor (página, componente, hook) puede
 * inspeccionar para decidir UX (modal confirm, redirect login, banner
 * de cupo agotado, etc.).
 *
 * Para meta-endpoints (`/api/health`, `/api` info) usar `apiMeta(...)`
 * directamente sin prefijo v1.
 */

const DEFAULT_BASE_URL = '' // proxy /api → localhost:3001 vía vite.config.ts
const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) || DEFAULT_BASE_URL

export class ApiException extends Error {
  status: number
  apiError: ApiError

  constructor(status: number, apiError: ApiError) {
    super(apiError.message || apiError.error || `HTTP ${status}`)
    this.status = status
    this.apiError = apiError
    this.name = 'ApiException'
  }
}

interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
  /** Si true, NO inyecta el prefijo /api/v1/ (para meta-endpoints como
   *  /api/health o /api info). */
  skipV1?: boolean
  /** Si true, NO inyecta Authorization header (para endpoints públicos
   *  cuando se añadan en el futuro). */
  skipAuth?: boolean
  /** AbortSignal para cancelar requests in-flight (race conditions en
   *  selectores con cargas en cascada — T-010 AdminZonePicker). */
  signal?: AbortSignal
  /** Query params como objeto. El cliente los serializa con URLSearchParams
   *  y los append al url final, descartando undefined/null. */
  query?: Record<string, string | number | boolean | null | undefined>
}

async function request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, skipV1 = false, skipAuth = false, signal, query } = options

  // Inyección del prefijo /api/v1/ para endpoints de negocio.
  const prefix = skipV1 ? '/api' : '/api/v1'
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  let url = `${BASE_URL}${prefix}${cleanPath}`

  // Query params: serializar con URLSearchParams, descartar undefined/null.
  if (query) {
    const sp = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue
      sp.append(k, String(v))
    }
    const qs = sp.toString()
    if (qs) {
      url += (url.includes('?') ? '&' : '?') + qs
    }
  }

  // Headers: JSON + auth (si aplica).
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  }
  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json'
  }
  if (!skipAuth) {
    const token = getAccessToken()
    if (token) {
      finalHeaders['Authorization'] = `Bearer ${token}`
    }
  }

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  // 204 No Content → resolver con null casteado.
  if (response.status === 204) {
    return null as T
  }

  // Parseo del body. Si no es JSON, devolver texto crudo en una excepción.
  let parsed: unknown
  try {
    parsed = await response.json()
  } catch {
    if (!response.ok) {
      throw new ApiException(response.status, {
        error: 'Invalid JSON response',
        message: `HTTP ${response.status} sin body JSON`,
      })
    }
    return null as T
  }

  if (!response.ok) {
    throw new ApiException(response.status, parsed as ApiError)
  }
  return parsed as T
}

/**
 * Llamada a un endpoint de negocio bajo /api/v1/*. El prefijo se
 * inyecta automáticamente — pasar solo el path relativo sin v1.
 *
 * @example
 *   const plans = await api('/billing/plans')
 *   // → GET /api/v1/billing/plans
 */
export function api<T = unknown>(
  path: string,
  options?: Omit<ApiRequestOptions, 'skipV1'>,
): Promise<T> {
  return request<T>(path, { ...options, skipV1: false })
}

/**
 * Llamada a un meta-endpoint SIN prefijo v1 (/api/health, /api info).
 *
 * @example
 *   const health = await apiMeta('/health')
 *   // → GET /api/health
 */
export function apiMeta<T = unknown>(
  path: string,
  options?: Omit<ApiRequestOptions, 'skipV1'>,
): Promise<T> {
  return request<T>(path, { ...options, skipV1: true })
}
